/**
 * OperatorPraticaDetailPage — F-007 Phase 4 (IMPL-P4-005),
 * F-008 Phase 7 (IMPL-P7-002).
 *
 * Detail-view di una pratica per l'operatore. Layout split:
 *   - sinistra: PDF preview (presigned URL TTL 60s)
 *   - destra:   metadati, datiStrutturati, azioni
 *
 * Azioni disponibili:
 *   - "Rilascia" → useReleaseInCarico (torna in INVIATA)
 *   - "Segnala errore" → apre `SegnalazioneErroreModal`
 *   - "Carica ricevuta completata" → apre `UploadRicevutaModal` (F-008).
 *     Visibile solo se `pratica.stato ∈ {IN_ELABORAZIONE, IN_CARICO}` AND
 *     `pratica.assignedOperatorId === currentUser.id`. Dopo il commit la
 *     query è invalidata da `useUploadRicevuta` e il CTA scompare
 *     automaticamente (stato → COMPLETATA).
 */
import * as React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthSession } from '@/features/auth/hooks/useAuthSession';
import { OperatorLayout } from '../components/OperatorLayout';
import { PraticaDetailSplit } from '../components/PraticaDetailSplit';
import { PDFPreviewPane } from '../components/PDFPreviewPane';
import { SegnalazioneErroreModal } from '../components/SegnalazioneErroreModal';
import { UploadRicevutaModal } from '../components/UploadRicevutaModal';
import { useReleaseInCarico } from '../hooks/useReleaseInCarico';
import { operatorClient } from '../lib/operator-client';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function OperatorPraticaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const praticaId = id ?? '';

  const detailQuery = useQuery({
    queryKey: ['operator', 'detail', praticaId] as const,
    queryFn: () => operatorClient.getDetail(praticaId),
    enabled: Boolean(praticaId),
  });

  const releaseMutation = useReleaseInCarico();
  const [segnalaOpen, setSegnalaOpen] = React.useState(false);
  const [isUploadModalOpen, setUploadModalOpen] = React.useState(false);
  const { user: currentUser } = useAuthSession();

  if (!praticaId) {
    return (
      <OperatorLayout title="Dettaglio pratica">
        <p
          role="alert"
          className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          ID pratica mancante.
        </p>
      </OperatorLayout>
    );
  }

  if (detailQuery.isLoading) {
    return (
      <OperatorLayout title="Dettaglio pratica">
        <p
          role="status"
          aria-live="polite"
          className="text-sm text-muted-foreground"
        >
          Caricamento pratica…
        </p>
      </OperatorLayout>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <OperatorLayout title="Dettaglio pratica">
        <p
          role="alert"
          className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          Impossibile caricare la pratica.
        </p>
        <Link to="/operatore" className="text-sm text-primary hover:underline">
          ← Torna alla coda
        </Link>
      </OperatorLayout>
    );
  }

  const pratica = detailQuery.data;

  // F-008 Phase 7 (IMPL-P7-002): CTA "Carica ricevuta completata" visibility.
  // Visible only when stato ∈ {IN_ELABORAZIONE, IN_CARICO} AND the pratica is
  // assigned to the current operator. Hidden for COMPLETATA, BLOCCATA, BOZZA,
  // ARCHIVED — and hidden if assigned to a different operator.
  const canUploadRicevuta = Boolean(
    pratica &&
      (pratica.stato === 'IN_ELABORAZIONE' ||
        pratica.stato === 'IN_CARICO') &&
      pratica.assignedOperatorId === currentUser?.id,
  );

  return (
    <OperatorLayout>
      <header className="space-y-1">
        <Link
          to="/operatore"
          className="text-xs text-muted-foreground hover:underline"
        >
          ← Coda operatore
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            Pratica <span className="font-mono">{pratica.id.slice(0, 8)}</span>
          </h1>
          <Badge variant="outline">{pratica.stato}</Badge>
          <Badge variant="secondary">{pratica.tipologia}</Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Inviata: {formatDateTime(pratica.submittedAt)} · Creata:{' '}
          {formatDateTime(pratica.createdAt)}
        </p>
      </header>

      <PraticaDetailSplit
        left={<PDFPreviewPane praticaId={pratica.id} />}
        right={
          <>
            <section className="rounded-md border p-4">
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Assegnazione
              </h2>
              <dl className="grid grid-cols-[max-content,1fr] gap-x-3 gap-y-1 text-sm">
                <dt className="text-muted-foreground">Operatore</dt>
                <dd>{pratica.assignedOperatorName ?? '—'}</dd>
                <dt className="text-muted-foreground">Da</dt>
                <dd>{formatDateTime(pratica.assignedAt)}</dd>
                {pratica.blockedAt && (
                  <>
                    <dt className="text-muted-foreground">Bloccata il</dt>
                    <dd>{formatDateTime(pratica.blockedAt)}</dd>
                    <dt className="text-muted-foreground">Motivi</dt>
                    <dd>{pratica.blockedReasons.join(', ') || '—'}</dd>
                    {pratica.blockedDetail && (
                      <>
                        <dt className="text-muted-foreground">Dettaglio</dt>
                        <dd className="whitespace-pre-wrap">
                          {pratica.blockedDetail}
                        </dd>
                      </>
                    )}
                  </>
                )}
              </dl>
            </section>

            <section className="rounded-md border p-4">
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Dati strutturati
              </h2>
              <pre className="max-h-[400px] overflow-auto rounded bg-muted/30 p-3 text-xs">
                {JSON.stringify(pratica.datiStrutturati, null, 2)}
              </pre>
            </section>

            <section className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => releaseMutation.mutate(pratica.id)}
                disabled={
                  releaseMutation.isPending || pratica.stato !== 'IN_CARICO'
                }
                aria-disabled={
                  releaseMutation.isPending || pratica.stato !== 'IN_CARICO'
                }
              >
                {releaseMutation.isPending ? 'Rilascio…' : 'Rilascia'}
              </Button>

              <Button
                type="button"
                variant="destructive"
                onClick={() => setSegnalaOpen(true)}
                disabled={pratica.stato !== 'IN_CARICO'}
              >
                Segnala errore
              </Button>

              {canUploadRicevuta && (
                <Button
                  type="button"
                  variant="default"
                  onClick={() => setUploadModalOpen(true)}
                >
                  Carica ricevuta completata
                </Button>
              )}
            </section>
          </>
        }
      />

      <SegnalazioneErroreModal
        praticaId={pratica.id}
        open={segnalaOpen}
        onClose={() => setSegnalaOpen(false)}
        clientName={pratica.assignedOperatorName ?? ''}
      />

      <UploadRicevutaModal
        open={isUploadModalOpen}
        praticaId={pratica.id}
        clientName={pratica.assignedOperatorName ?? ''}
        onCompleted={() => {
          setUploadModalOpen(false);
          toast.success('Ricevuta caricata. Pratica completata.');
        }}
        onClose={() => setUploadModalOpen(false)}
      />
    </OperatorLayout>
  );
}
